// src/belediye/components/mesajlasma/DuePicker.js
// FAZ-55 — SÜRE (tarih + saat) seçici.
//
// NEDEN: eskiden özel süre `input[type=datetime-local]` ile seçiliyordu.
// Kullanıcı: "saat ve tarih seçimi çok zorlayıcı, farklı bir format olsun."
// datetime-local tek kutuda gün/ay/yıl/saat/dakika ister; klavyeyle segment
// segment gezmek gerekir, dokunmatikte iyice zahmetlidir.
//
// FAZ-55/8 (kullanıcı kararı 2026-07-28) — HAZIR METİN / ELLE AYRIMI NETLEŞTİ:
//   "süre kısmında hazır metinler olsun … kendisi süre belirlemek isterse
//    burada SADECE ekrana bir takvim ve yanında saat sayacı gelsin ve 'kendin
//    süre belirle' kısmına tıklayınca DİREKT açılsın bu pencere."
// Yani hazır seçenekler (Bugün / Yarın / 3 gün / 1 hafta) adımın KENDİSİNDE
// duruyor; bu bileşen artık onları TEKRARLAMAZ. Burada yalnız iki şey var:
//   1) TAKVİM      — gün
//   2) SAAT SAYACI — saat/dakika, yukarı-aşağı oklarla
// Eski sürümde ikisinin de üstünde ayrıca gün ve saat çipleri vardı; adımdaki
// hazır seçeneklerin birebir kopyasıydı ve "hangisi geçerli" sorusunu doğuruyordu.
//
// Sözleşme: onDone({ due_date: "YYYY-MM-DD"|null, due_time: "HH:MM"|null }).
// Saat seçilmezse due_time null → backend "gün sonu" kabul eder (FAZ-43).
import React, { useMemo, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

import { isoLocalDate } from "../../utils/dateLocal";
import SihirbazAltSatir from "./SihirbazAltSatir";

const uzunTarih = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "short" });
};

// ── FAZ-55/5: TAKVİM IZGARASI ────────────────────────────────────────────────
// Kullanıcı: "özel tarih seç kısmı için tarih kısmını düzgün seç, şu anki
// yapısı çok çirkin." Eski hâl `input[type=date]` idi: görünüşü tarayıcıya
// göre değişiyordu, dokunmatikte ayrı bir sistem penceresi açıyordu ve
// çiplerle aynı dili konuşmuyordu. Yerine kendi ay ızgaramız: gün rakamlarına
// tek dokunuş, ay okları, geçmiş günler kapalı.
const GUN_BASLIKLARI = ["Pt", "Sa", "Ça", "Pe", "Cu", "Ct", "Pz"];

const iso = (y, ay, gun) =>
  `${y}-${String(ay + 1).padStart(2, "0")}-${String(gun).padStart(2, "0")}`;

function Takvim({ secili, enErken, onSec }) {
  const bas = secili ? new Date(`${secili}T00:00:00`) : new Date(`${enErken}T00:00:00`);
  const [ay, setAy] = useState({ y: bas.getFullYear(), m: bas.getMonth() });

  const hucreler = useMemo(() => {
    const ilk = new Date(ay.y, ay.m, 1);
    // Pazartesi = 0 olacak şekilde kaydır (JS'te Pazar = 0).
    const bosluk = (ilk.getDay() + 6) % 7;
    const gunSayisi = new Date(ay.y, ay.m + 1, 0).getDate();
    return [
      ...Array.from({ length: bosluk }, () => null),
      ...Array.from({ length: gunSayisi }, (_, i) => i + 1),
    ];
  }, [ay]);

  const ayAdi = new Date(ay.y, ay.m, 1).toLocaleDateString("tr-TR", {
    month: "long",
    year: "numeric",
  });
  const kaydir = (n) => {
    const d = new Date(ay.y, ay.m + n, 1);
    setAy({ y: d.getFullYear(), m: d.getMonth() });
  };

  return (
    <div className="msj-takvim">
      <div className="msj-takvim-bas">
        <button type="button" className="msj-takvim-ok" onClick={() => kaydir(-1)} aria-label="Önceki ay">
          <ChevronLeftIcon />
        </button>
        <span className="msj-takvim-ay">{ayAdi}</span>
        <button type="button" className="msj-takvim-ok" onClick={() => kaydir(1)} aria-label="Sonraki ay">
          <ChevronRightIcon />
        </button>
      </div>
      <div className="msj-takvim-izgara">
        {GUN_BASLIKLARI.map((g) => (
          <span key={g} className="msj-takvim-gunbas">{g}</span>
        ))}
        {hucreler.map((g, i) => {
          if (g === null) return <span key={`b${i}`} />;
          const deger = iso(ay.y, ay.m, g);
          return (
            <button
              key={deger}
              type="button"
              className={`msj-takvim-gun ${secili === deger ? "is-secili" : ""}`}
              disabled={deger < enErken}
              onClick={() => onSec(deger)}
            >
              {g}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── FAZ-55/8+10: SAAT SAYACI ────────────────────────────────────────────────
// Kullanıcı: "yanında saat sayacı gelsin." `input[type=time]` tarayıcıya göre
// değişen, dokunmatikte ayrı pencere açan bir denetimdi; yerine iki sütunlu
// sayaç. Dakika 5'er adım (görev süresinde dakika hassasiyeti gerekmiyor,
// 60 ayrı değeri tek tek gezmek işkence).
//
// FAZ-55/10 (kullanıcı, 2026-07-28): "saat kısmını kaydırmalı yap, hızlı şekilde
// atlayabileyim; aynı şekilde seçip de direkt yazabileyim."
//   • KAYDIR  → alan üzerinde tekerlek/iki-parmak: bir adım ileri/geri.
//   • YAZ     → alanın kendisi bir input; 17 yazıp geçebilirsin.
//   • TIKLA   → yukarı/aşağı okları duruyor (dokunmatikte en kolayı).
const DAKIKA_ADIM = 5;

// DİKKAT: bu bileşen SaatSayaci'nin DIŞINDA tanımlıdır. İçeride tanımlanırsa
// her render'da yeni bir bileşen TİPİ doğar; React eskisini söker, yenisini
// takar — DOM düğümü değişir, arka arkaya tıklamalar kaybolur (ilk tık işler,
// ikincisi ölü düğüme gider). Ölçüldü: sayaç 17→16'da takılıyordu.
function Sutun({ etiket, deger, onYaz, artir, azalt, pasif }) {
  return (
    <div className="msj-sayac-sutun">
      <button type="button" className="msj-sayac-ok" onClick={artir} disabled={pasif} aria-label={`${etiket} artır`}>
        <ChevronUpIcon />
      </button>
      <input
        type="text"
        inputMode="numeric"
        className="msj-sayac-deger"
        aria-label={etiket}
        disabled={pasif}
        value={deger}
        onChange={(e) => onYaz(e.target.value.replace(/\D/g, "").slice(0, 2))}
        // Tekerlek = bir adım. preventDefault YOK: pasif dinleyici uyarısı
        // vermesin diye yalnız değeri değiştiriyoruz, sayfa kaydırması
        // kısa alanda zaten hissedilmiyor.
        onWheel={(e) => (e.deltaY < 0 ? artir() : azalt())}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") { e.preventDefault(); artir(); }
          if (e.key === "ArrowDown") { e.preventDefault(); azalt(); }
        }}
      />
      <button type="button" className="msj-sayac-ok" onClick={azalt} disabled={pasif} aria-label={`${etiket} azalt`}>
        <ChevronDownIcon />
      </button>
    </div>
  );
}

function SaatSayaci({ deger, onDegis, pasif }) {
  // deger "" (gün sonu) ya da "HH:MM".
  const [ss, dd] = deger ? deger.split(":").map((n) => parseInt(n, 10)) : [17, 0];
  const yaz = (h, m) =>
    onDegis(`${String((h + 24) % 24).padStart(2, "0")}:${String((m + 60) % 60).padStart(2, "0")}`);
  // Elle yazarken: boş/yarım giriş kabul edilir, sınır dışına taşan değer
  // kırpılır (23'ten büyük saat, 59'dan büyük dakika olmaz).
  const yazSaat = (v) => yaz(Math.min(23, parseInt(v || "0", 10) || 0), dd);
  const yazDakika = (v) => yaz(ss, Math.min(59, parseInt(v || "0", 10) || 0));
  // Sayaç iki durumda soluk: gün henüz seçilmedi ya da "Gün sonu" seçili
  // (o zaman saatin bir hükmü yok).
  const sayacPasif = pasif || deger === "";

  return (
    <div className="msj-sayac">
      <div className="msj-sayac-govde">
        <Sutun
          etiket="Saat"
          pasif={sayacPasif}
          deger={String(ss).padStart(2, "0")}
          onYaz={yazSaat}
          artir={() => yaz(ss + 1, dd)}
          azalt={() => yaz(ss - 1, dd)}
        />
        <span className="msj-sayac-iki">:</span>
        <Sutun
          etiket="Dakika"
          pasif={sayacPasif}
          deger={String(dd).padStart(2, "0")}
          onYaz={yazDakika}
          artir={() => yaz(ss, dd + DAKIKA_ADIM)}
          azalt={() => yaz(ss, dd - DAKIKA_ADIM)}
        />
      </div>
      {/* Saat vermek ZORUNLU değil — "gün sonu" saatsiz süredir (FAZ-43).
          FAZ-83/T1 (kullanıcı, 2026-08-01): tek "Gün sonu" çipi vardı ve
          sayaçta 17:00 yazıyordu — ama o 17:00 GEÇERLİ DEĞİLDİ, yalnız
          sayacın başlangıç görüntüsüydü. "Hangisi geçerli" sorusunu doğuran
          bu ikilik, iki çipli açık bir seçime dönüştü: hangisi seçiliyse o
          geçerlidir, sayaç da ona göre soluklaşır. */}
      <div className="msj-due-cipler msj-saat-kip">
        <button
          type="button"
          className={`msj-due-cip ${deger === "" ? "is-secili" : ""}`}
          onClick={() => onDegis("")}
          disabled={pasif}
        >
          Gün sonu
        </button>
        <button
          type="button"
          className={`msj-due-cip ${deger !== "" ? "is-secili" : ""}`}
          onClick={() => onDegis(deger || "17:00")}
          disabled={pasif}
        >
          Saat ver
        </button>
      </div>
    </div>
  );
}

export default function DuePicker({
  initialDate = "",
  initialTime = "",
  onDone,
  // FAZ-55/10+11 — düğmeler BAĞLAMA göre; hepsi aynı anda ASLA çıkmaz:
  //   • Sihirbaz (görev açarken) → yalnız ATLA. Henüz kaldırılacak bir süre
  //     yok; "vazgeç" de geri okunun işi.
  //   • Süre GÜNCELLEME paneli  → solda SÜREYİ KALDIR çipi, sağda VAZGEÇ.
  // Ortak: sağ uçta Gönder. Atla/Vazgeç hep gönderin SOLUNDA, aynı yerde.
  onCancel,
  onRemove,
  // FAZ-55/11: sihirbazda ATLA (adımı geç), güncelleme panelinde YOK.
  onSkip,
  // FAZ-83/S1 — GÖMÜLÜ KİP: "Aciliyet ve süre" adımının içinde, süre
  // çiplerinin hemen ALTINDA açılır. Kendi onay satırını taşımaz; seçim
  // değiştikçe `onDegis({due_date, due_time})` ile yukarı bildirir.
  gomulu = false,
  onDegis,
}) {
  const [tarih, setTarih] = useState(initialDate || "");
  const [saat, setSaat] = useState(initialTime || "");

  const bugun = isoLocalDate();

  // Tek kapı: tarih ya da saat değişince ikisini birden yukarı bildir.
  const bildir = (t, s) => onDegis && onDegis({ due_date: t || null, due_time: t && s ? s : null });
  const tarihSec = (t) => {
    setTarih(t);
    bildir(t, saat);
  };
  const saatSec = (s) => {
    setSaat(s);
    bildir(tarih, s);
  };

  return (
    <div className={`msj-due ${gomulu ? "is-gomulu" : ""}`}>
      {/* Takvim ve sayaç YAN YANA (dar ekranda alt alta) — kullanıcı: "ekrana
          bir takvim ve YANINDA saat sayacı gelsin". FAZ-55/10: takvim panelin
          tamamını kaplamıyor, sayaç ona yaklaştı. */}
      <div className="msj-due-govde">
        <Takvim secili={tarih} enErken={bugun} onSec={tarihSec} />
        <SaatSayaci deger={saat} onDegis={saatSec} pasif={!tarih} />
      </div>

      {/* FAZ-83/B1 — TEK onay satırı, ortak bileşenden: ATLA/VAZGEÇ sol uçta,
          GÖNDER sağ uçta, arada seçimin canlı özeti. "Süreyi kaldır" çipi
          (yalnız süre GÜNCELLEME panelinde) en solda kalır.
          Gömülü kipte bu satır hiç çizilmez — adımın kendi alt satırı var. */}
      {!gomulu && (
        <SihirbazAltSatir
          sinif="msj-due-onay"
          sol={
            onRemove ? (
              <button
                type="button"
                className="msj-due-cip msj-due-kaldir"
                onClick={onRemove}
                title="Süre olmadan devam et"
              >
                <ClockIcon /> Süreyi kaldır
              </button>
            ) : null
          }
          onSkip={onSkip || onCancel}
          skipEtiket={onSkip ? "Atla" : "Vazgeç"}
          skipTitle={onSkip ? "Süre vermeden devam et" : "Seçimden vazgeç"}
          ozet={
            tarih ? (
              <>
                <b>{uzunTarih(tarih)}</b>
                {saat ? ` · ${saat}` : " · gün sonu"}
              </>
            ) : (
              "Takvimden bir gün seçin"
            )
          }
          gonderAktif={!!tarih}
          onGonder={() => tarih && onDone({ due_date: tarih, due_time: saat || null })}
          gonderTitle="Bu süreyi kullan"
        />
      )}
    </div>
  );
}
